<div class="organization-projects crm-page">
  @if (isLoading) {
    <div class="crm-page__overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ "MESSAGES.LOADING" | translate }}</span>
      </div>
    </div>
  }

  <div class="crm-page__toolbar">
    <div class="organization-projects__toolbar-spacer"></div>

    <div class="organization-projects__actions">
      <button
        type="button"
        #projectPopover="ngbPopover"
        class="overview-btn overview-btn-primary"
        [ngbTooltip]="'BUTTONS.ADD_PROJECT' | translate"
        [ngbPopover]="addProjectForm"
        popoverClass="popover-lg project-files-rename-popover"
        autoClose="outside"
        placement="bottom-end"
      >
        + {{ "BUTTONS.ADD_PROJECT" | translate }}
      </button>
    </div>
  </div>

  <ng-template #addProjectForm>
    <div
      [formGroup]="newOrganizationProjectForm.form"
      class="files-rename-form organization-projects__popover-form"
    >
      <app-search
        formControlName="newProjectId"
        [items]="(companyProjects$ | async)!"
        [filterFn]="projectTitleFilter"
        [formatter]="projectFormatter"
        [compareFn]="projectCompareFn"
        [placeholderText]="'PLACEHOLDERS.PROJECT_TITLE' | translate"
        [errorMessage]="'ERRORS.INCORRECT_SELECTED_PROJECT' | translate"
        [resultTemplate]="customTemplate"
        customClass="files-rename-input input-sm organization-projects__popover-search"
      >
        <ng-template #customTemplate let-r="result" let-t="term">
          @if (r) {
            <div class="organization-projects__search-result">
              @if (r.imageUrl !== notFound) {
                <div class="organization-projects__project-avatar">
                  <img [src]="r.imageUrl" alt="project image" />
                </div>
              } @else {
                <div
                  class="organization-projects__project-avatar organization-projects__project-avatar--fallback"
                  [ngStyle]="{ backgroundColor: r.color }"
                >
                  <span>{{ r.title | abbreviation }}</span>
                </div>
              }

              <ngb-highlight [result]="r.title" [term]="t"></ngb-highlight>
            </div>
          }
        </ng-template>
      </app-search>

      <button
        type="button"
        class="files-rename-action files-rename-action--confirm"
        [disabled]="newOrganizationProjectForm.form.invalid"
        (click)="addProject(projectPopover)"
      >
        {{ "BUTTONS.ADD" | translate }}
      </button>
    </div>

    @if (canCreateProjects$ | async) {
      <button
        class="organization-projects__popover-secondary"
        type="button"
        (click)="openCreateProjectModal(projectPopover)"
      >
        + {{ "BUTTONS.ADD_PROJECT" | translate }}
      </button>
    }
  </ng-template>

  @if (organizationProjects$ | async; as projects) {
    @if (!projects.length) {
      <app-card-shell class="crm-page__empty-card" padding="none">
        <div class="crm-page__empty-state">
          {{ "MESSAGES.EMPTY_LIST" | translate: { param: ("TITLES.PROJECTS" | translate) } }}
        </div>
      </app-card-shell>
    } @else {
      <app-card-shell class="crm-page__list-card" padding="none">
        <div class="crm-table__desktop">
          <div class="crm-table__scroller">
            <table class="crm-table__table organization-projects__table">
              <thead>
                <tr class="crm-table__header-row">
                  <th class="crm-table__header-cell organization-projects__title-column">
                    {{ "TABLE_HEADERS.TITLE" | translate }}
                  </th>
                  <th class="crm-table__header-cell organization-projects__count-column">
                    {{ "TABLE_HEADERS.EMPLOYEES_COUNT" | translate }}
                  </th>
                  <th class="crm-table__header-cell">
                    {{ "TABLE_HEADERS.ASSIGNMENTS" | translate }}
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (project of projects; track project.id) {
                  <tr class="crm-table__row" [class.organization-projects__row--archived]="project.isArchived">
                    <td class="crm-table__cell crm-table__cell--title">
                      <a
                        [routerLink]="['../../../', 'projects', project.id]"
                        [ngbTooltip]="project.isArchived ? ('TOOLTIPS.ARCHIVED_PROJECT' | translate) : ''"
                        class="organization-projects__title-link"
                      >
                        @if (project.imageUrl !== notFound) {
                          <div class="organization-projects__project-avatar">
                            <img [src]="project.imageUrl" alt="project image" />
                          </div>
                        } @else {
                          <div
                            class="organization-projects__project-avatar organization-projects__project-avatar--fallback"
                            [ngStyle]="{ backgroundColor: project.color }"
                          >
                            <span>{{ project.title | abbreviation }}</span>
                          </div>
                        }

                        <span>{{ project.title }}</span>
                      </a>
                    </td>

                    <td class="crm-table__cell">{{ project.employeesCount }}</td>

                    <td class="crm-table__cell">
                      <div class="organization-projects__status-list">
                        @if (project.overdueAssignmentsCount) {
                          <span
                            class="organization-projects__status-pill organization-projects__status-pill--overdue status-overdue"
                            [ngbTooltip]="'TOOLTIPS.OVERDUE_ASSIGNMENTS' | translate"
                          >
                            {{ project.overdueAssignmentsCount }}
                          </span>
                        }

                        @if (project.activeAssignmentsCount) {
                          <span
                            class="organization-projects__status-pill organization-projects__status-pill--active status-active"
                            [ngbTooltip]="'TOOLTIPS.ACTIVE_ASSIGNMENTS' | translate"
                          >
                            {{ project.activeAssignmentsCount }}
                          </span>
                        }

                        @if (project.newAssignmentsCount) {
                          <span
                            class="organization-projects__status-pill organization-projects__status-pill--new status-new"
                            [ngbTooltip]="'TOOLTIPS.NEW_ASSIGNMENTS' | translate"
                          >
                            {{ project.newAssignmentsCount }}
                          </span>
                        }

                        @if (project.postponedAssignmentsCount) {
                          <span
                            class="organization-projects__status-pill organization-projects__status-pill--postponed status-postponed"
                            [ngbTooltip]="'TOOLTIPS.POSTPONED_ASSIGNMENTS' | translate"
                          >
                            {{ project.postponedAssignmentsCount }}
                          </span>
                        }

                        @if (project.completedAssignmentsCount) {
                          <span
                            class="organization-projects__status-pill organization-projects__status-pill--completed status-completed"
                            [ngbTooltip]="'TOOLTIPS.COMPLETED_ASSIGNMENTS' | translate"
                          >
                            {{ project.completedAssignmentsCount }}
                          </span>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </app-card-shell>

      <section class="crm-table__mobile-section">
        <div class="crm-table__mobile-list">
          @for (project of displayedProjects$ | async; track project.id) {
            <article class="crm-mobile-card" [class.organization-projects__row--archived]="project.isArchived">
              <div class="crm-mobile-card__header">
                <div class="crm-mobile-card__title-wrap">
                  @if (project.imageUrl !== notFound) {
                    <div class="organization-projects__project-avatar">
                      <img [src]="project.imageUrl" alt="project image" />
                    </div>
                  } @else {
                    <div
                      class="organization-projects__project-avatar organization-projects__project-avatar--fallback"
                      [ngStyle]="{ backgroundColor: project.color }"
                    >
                      <span>{{ project.title | abbreviation }}</span>
                    </div>
                  }

                  <div class="crm-mobile-card__title-block">
                    <a [routerLink]="['../../../', 'projects', project.id]" class="crm-mobile-card__title">
                      {{ project.title }}
                    </a>
                  </div>
                </div>
              </div>

              <div class="crm-mobile-card__grid">
                <div class="crm-mobile-card__field">
                  <p class="crm-mobile-card__label">
                    {{ "TABLE_HEADERS.EMPLOYEES_COUNT" | translate }}
                  </p>
                  <p class="crm-mobile-card__value">
                    {{ project.employeesCount }}
                  </p>
                </div>

                <div class="crm-mobile-card__field">
                  <p class="crm-mobile-card__label">
                    {{ "TABLE_HEADERS.ASSIGNMENTS" | translate }}
                  </p>
                  <div class="organization-projects__status-list organization-projects__status-list--mobile">
                    @if (project.overdueAssignmentsCount) {
                      <span
                        class="organization-projects__status-pill organization-projects__status-pill--overdue status-overdue"
                        [ngbTooltip]="'TOOLTIPS.OVERDUE_ASSIGNMENTS' | translate"
                      >
                        <span>{{ project.overdueAssignmentsCount }}</span>
                        <span class="organization-projects__status-pill-label">
                          {{ "ASSIGNMENT_STATUSES_PLURAL.OVERDUE" | translate }}
                        </span>
                      </span>
                    }
                    @if (project.activeAssignmentsCount) {
                      <span
                        class="organization-projects__status-pill organization-projects__status-pill--active status-active"
                        [ngbTooltip]="'TOOLTIPS.ACTIVE_ASSIGNMENTS' | translate"
                      >
                        <span>{{ project.activeAssignmentsCount }}</span>
                        <span class="organization-projects__status-pill-label">
                          {{ "ASSIGNMENT_STATUSES_PLURAL.ACTIVE" | translate }}
                        </span>
                      </span>
                    }
                    @if (project.newAssignmentsCount) {
                      <span
                        class="organization-projects__status-pill organization-projects__status-pill--new status-new"
                        [ngbTooltip]="'TOOLTIPS.NEW_ASSIGNMENTS' | translate"
                      >
                        <span>{{ project.newAssignmentsCount }}</span>
                        <span class="organization-projects__status-pill-label">
                          {{ "ASSIGNMENT_STATUSES_PLURAL.NEW" | translate }}
                        </span>
                      </span>
                    }
                    @if (project.postponedAssignmentsCount) {
                      <span
                        class="organization-projects__status-pill organization-projects__status-pill--postponed status-postponed"
                        [ngbTooltip]="'TOOLTIPS.POSTPONED_ASSIGNMENTS' | translate"
                      >
                        <span>{{ project.postponedAssignmentsCount }}</span>
                        <span class="organization-projects__status-pill-label">
                          {{ "ASSIGNMENT_STATUSES_PLURAL.POSTPONED" | translate }}
                        </span>
                      </span>
                    }
                    @if (project.completedAssignmentsCount) {
                      <span
                        class="organization-projects__status-pill organization-projects__status-pill--completed status-completed"
                        [ngbTooltip]="'TOOLTIPS.COMPLETED_ASSIGNMENTS' | translate"
                      >
                        <span>{{ project.completedAssignmentsCount }}</span>
                        <span class="organization-projects__status-pill-label">
                          {{ "ASSIGNMENT_STATUSES_PLURAL.COMPLETED" | translate }}
                        </span>
                      </span>
                    }
                  </div>
                </div>
              </div>
            </article>
          }
        </div>

        @if (projects.length > 3) {
          <button class="organization-projects__toggle" type="button" (click)="toggleProjects()">
            {{ (showAll$.value ? "BUTTONS.SHOW_LESS" : "BUTTONS.SHOW_MORE") | translate }}
          </button>
        }
      </section>
    }
  }

</div>
